import { KpiCard, KpiGrid, type TonoKpi } from "@/components/ds";
import type { AsignacionesKpis } from "@/types/capacitaciones-ia";

/**
 * Semáforo de cobertura: sobre el 80% de los trabajadores al día la empresa
 * está cumpliendo, bajo el 50% es un incumplimiento que hay que atender hoy, y
 * en el medio es una advertencia. Los cortes son los del prototipo
 * (`Programas.dc.html`) y se aplican al PORCENTAJE, no al valor absoluto: 8 de
 * 10 y 80 de 100 son el mismo estado de salud.
 *
 * Sin trabajadores alcanzados no hay nada que juzgar todavía — pintarlo verde
 * ("100% al día") mentiría, así que queda neutro.
 */
function tonoCobertura(alDia: number, total: number): TonoKpi {
  if (total === 0) return "neutro";
  const pct = Math.round((alDia / total) * 100);
  if (pct >= 80) return "exito";
  if (pct >= 50) return "advertencia";
  return "critico";
}

interface CapacitacionesKpiHeaderProps {
  kpis: AsignacionesKpis | null;
  /** Formatea una fecha "Y-m-d"/ISO como texto local (mismo helper que usa
   * el resto de la pantalla — nunca `new Date("Y-m-d")` a secas, que en
   * Chile dibuja el día anterior). */
  formatFecha: (iso: string | null) => string;
}

/**
 * Cabecera de 3 KPIs del listado fusionado de Programas (D9, WU5 —
 * sdd/capacitaciones-pantalla-unica). Puramente presentacional: el dato
 * viene ya resuelto de `GET .../asignaciones/kpis` (el mismo endpoint que
 * alimentaba la retirada Pantalla 4 "Avance y Resultados" — ver
 * `AsignacionesKpisController`, que sobrevivió a la fusión para esto).
 *
 * Mientras `kpis` es `null` (primera carga) no renderiza nada — el
 * `isLoading` de la pantalla ya cubre el estado de carga con su propio
 * spinner, un segundo esqueleto acá sería ruido.
 */
export function CapacitacionesKpiHeader({ kpis, formatFecha }: CapacitacionesKpiHeaderProps) {
  if (!kpis) return null;

  return (
    <KpiGrid>
      <KpiCard
        label="Trabajadores al día"
        icono="task_alt"
        valor={`${kpis.trabajadores_al_dia}/${kpis.trabajadores_total}`}
        tono={tonoCobertura(kpis.trabajadores_al_dia, kpis.trabajadores_total)}
        detalle={
          kpis.trabajadores_total > 0
            ? "respondieron todas sus capacitaciones asignadas"
            : "todavía no hay trabajadores con capacitaciones asignadas"
        }
      />
      {/* Una fecha no se juzga: el próximo envío no está "bien" ni "mal". El
          único tono acá distingue "hay algo agendado" de "no hay nada", que sí
          es un hecho accionable. */}
      <KpiCard
        label="Próximo envío"
        icono="event"
        valor={kpis.proximo_envio ? formatFecha(kpis.proximo_envio) : "—"}
        tono={kpis.proximo_envio ? "exito" : "neutro"}
        detalle={
          kpis.proximo_envio
            ? "próxima temática con fecha reservada"
            : "ninguna temática tiene una fecha reservada"
        }
      />
      {/* Advertencia, no crítico: un documento sin programa es trabajo
          pendiente de la empresa, no un incumplimiento legal en curso. */}
      <KpiCard
        label="Documentos sin programa"
        icono="description"
        valor={kpis.documentos_sin_programa}
        tono={kpis.documentos_sin_programa > 0 ? "advertencia" : "exito"}
        detalle="marcados como fuente de capacitación, sin programa generado"
      />
    </KpiGrid>
  );
}
